import { useState } from 'react'
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogBackdrop,
  DialogPositioner,
  Button,
  Box,
  IconButton,
  HStack,
} from '@chakra-ui/react'
import { X, ArrowLeft } from 'lucide-react'
import { UnitMutationsHistory } from './UnitMutationsHistory'
import { Step1MutationType } from './mutation-steps/Step1MutationType'
import { Step2Parties } from './mutation-steps/Step2Parties'
import { Step3Details } from './mutation-steps/Step3Details'
import { CreateUnitMutationInput, validateMutationShares } from '../types/mutations'
import { useCreateUnitMutation } from '../hooks/useUnitMutations'
import { toaster } from '@/shared/lib/toaster'

interface UnitMutationsManagementModalProps {
  isOpen: boolean
  onClose: () => void
  unitId: string
  unitNumber: string
  currentOwners: Array<{
    ownerId: string
    ownerName: string
    ownershipId: string
    shareNumerator: number
    shareDenominator: number
    ownershipType?: 'full' | 'usufruct' | 'bare'
  }>
}

type ViewMode = 'history' | 'add-step1' | 'add-step2' | 'add-step3'

export function UnitMutationsManagementModal({
  isOpen,
  onClose,
  unitId,
  unitNumber,
  currentOwners,
}: UnitMutationsManagementModalProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('history')
  const createMutation = useCreateUnitMutation()

  // Données du formulaire
  const [formData, setFormData] = useState<Partial<CreateUnitMutationInput>>({
    unit_id: unitId,
    mutation_date: new Date().toISOString().split('T')[0],
    former_parties: currentOwners.map((owner) => ({
      owner_id: owner.ownerId,
      share_numerator: owner.shareNumerator,
      share_denominator: owner.shareDenominator,
      ownership_type: owner.ownershipType || 'full',
    })),
    new_parties: [],
  })

  const resetForm = () => {
    setFormData({
      unit_id: unitId,
      mutation_date: new Date().toISOString().split('T')[0],
      former_parties: currentOwners.map((owner) => ({
        owner_id: owner.ownerId,
        share_numerator: owner.shareNumerator,
        share_denominator: owner.shareDenominator,
        ownership_type: owner.ownershipType || 'full',
      })),
      new_parties: [],
    })
  }

  const handleClose = () => {
    setViewMode('history')
    resetForm()
    onClose()
  }

  const handleBackToHistory = () => {
    setViewMode('history')
    resetForm()
  }

  const handleStartAddMutation = () => {
    resetForm()
    setViewMode('add-step1')
  }

  const handleNext = () => {
    if (viewMode === 'add-step1') {
      setViewMode('add-step2')
    } else if (viewMode === 'add-step2') {
      setViewMode('add-step3')
    }
  }

  const handlePrevious = () => {
    if (viewMode === 'add-step3') {
      setViewMode('add-step2')
    } else if (viewMode === 'add-step2') {
      setViewMode('add-step1')
    }
  }

  const handleSubmit = async () => {
    try {
      if (!formData.mutation_type || !formData.mutation_date) {
        toaster.create({
          title: 'Erreur',
          description: 'Veuillez remplir tous les champs obligatoires',
          type: 'error',
        })
        return
      }

      if (!formData.former_parties || formData.former_parties.length === 0) {
        toaster.create({
          title: 'Erreur',
          description: 'Veuillez sélectionner au moins un ancien propriétaire',
          type: 'error',
        })
        return
      }

      if (!formData.new_parties || formData.new_parties.length === 0) {
        toaster.create({
          title: 'Erreur',
          description: 'Veuillez sélectionner au moins un nouveau propriétaire',
          type: 'error',
        })
        return
      }

      // Vérifier que tous les nouveaux propriétaires ont un owner_id valide
      const invalidNewParty = formData.new_parties.find(
        (party) => !party.owner_id || party.owner_id.trim() === ''
      )
      if (invalidNewParty) {
        toaster.create({
          title: 'Erreur',
          description: 'Tous les nouveaux propriétaires doivent être sélectionnés',
          type: 'error',
        })
        return
      }

      await createMutation.mutateAsync(formData as CreateUnitMutationInput)

      toaster.create({
        title: 'Mutation créée',
        description: 'La mutation a été enregistrée avec succès',
        type: 'success',
      })

      handleBackToHistory()
    } catch (error) {
      console.error('Error creating mutation:', error)
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Une erreur est survenue lors de la création de la mutation',
        type: 'error',
      })
    }
  }

  const canGoNext = () => {
    if (viewMode === 'add-step1') {
      return !!formData.mutation_type && !!formData.mutation_date
    }
    if (viewMode === 'add-step2') {
      // Vérifier qu'il y a au moins un ancien et un nouveau propriétaire
      if (!formData.former_parties || formData.former_parties.length === 0) {
        return false
      }
      if (!formData.new_parties || formData.new_parties.length === 0) {
        return false
      }
      // Vérifier que tous les nouveaux propriétaires ont un owner_id valide (non vide)
      const allNewPartiesHaveOwner = formData.new_parties.every(
        (party) => party.owner_id && party.owner_id.trim() !== ''
      )
      if (!allNewPartiesHaveOwner) {
        return false
      }
      // Vérifier que les parts correspondent par type de propriété
      const validation = validateMutationShares(
        formData.former_parties,
        formData.new_parties
      )
      if (!validation.valid) {
        return false
      }
      return true
    }
    return true
  }

  const getTitle = () => {
    if (viewMode === 'history') {
      return `Gestion des mutations - Lot ${unitNumber}`
    }
    const stepNumber = viewMode === 'add-step1' ? 1 : viewMode === 'add-step2' ? 2 : 3
    return `Nouvelle mutation - Lot ${unitNumber} - Étape ${stepNumber}/3`
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="xl">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent
          maxH="90vh"
          display="flex"
          flexDirection="column"
        >
        <DialogHeader borderBottomWidth="1px">
          <HStack justify="space-between" width="100%">
            <HStack gap={2}>
              {viewMode !== 'history' && (
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToHistory}
                  aria-label="Retour à l'historique"
                >
                  <ArrowLeft size={18} />
                </IconButton>
              )}
              <DialogTitle>{getTitle()}</DialogTitle>
            </HStack>
            <IconButton variant="ghost" size="sm" onClick={handleClose} aria-label="Fermer">
              <X size={18} />
            </IconButton>
          </HStack>
        </DialogHeader>

        <DialogBody overflowY="auto" flex={1}>
          {viewMode === 'history' && (
            <Box>
              <UnitMutationsHistory unitId={unitId} onAddMutation={handleStartAddMutation} />
            </Box>
          )}

          {viewMode === 'add-step1' && (
            <Box minH="400px">
              <Step1MutationType
                formData={formData}
                onUpdate={(data) => setFormData({ ...formData, ...data })}
              />
            </Box>
          )}

          {viewMode === 'add-step2' && (
            <Box minH="400px">
              <Step2Parties
                formData={formData}
                onUpdate={(data) => setFormData({ ...formData, ...data })}
                currentOwners={currentOwners}
              />
            </Box>
          )}

          {viewMode === 'add-step3' && (
            <Box minH="400px">
              <Step3Details
                formData={formData}
                onUpdate={(data) => setFormData({ ...formData, ...data })}
              />
            </Box>
          )}
        </DialogBody>

        {viewMode !== 'history' && (
          <Box borderTopWidth="1px" p={4}>
            <HStack justify="space-between" width="100%">
              <Button variant="ghost" onClick={handleBackToHistory}>
                Annuler
              </Button>

              <HStack gap={2}>
                {viewMode !== 'add-step1' && (
                  <Button variant="outline" onClick={handlePrevious}>
                    Précédent
                  </Button>
                )}

                {viewMode !== 'add-step3' ? (
                  <Button colorPalette="brand" onClick={handleNext} disabled={!canGoNext()}>
                    Suivant
                  </Button>
                ) : (
                  <Button
                    colorPalette="brand"
                    onClick={handleSubmit}
                    loading={createMutation.isPending}
                    disabled={!canGoNext()}
                  >
                    Enregistrer
                  </Button>
                )}
              </HStack>
            </HStack>
          </Box>
        )}
      </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
